import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { DEFAULT_FIELD_COLOR } from './colors';
import { fieldKeys } from './queries';
import type { SupabaseClient } from '../../lib/supabase';
import type { FieldValueRow } from './fieldChips';

/**
 * Defining a project's own fields.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Fields arrived with the Social Media Style template and could only exist if
 * a template seeded them. Kyu: "this is the template I wanted all along... can
 * we edit the code so other projects that are non Social Media can do this
 * too?" A capability that only one template can grant is not a capability, it
 * is a decoration on that template.
 *
 * ── Who may ────────────────────────────────────────────────────────────────
 *
 * Project MANAGERS, enforced by RLS on both tables (20261029000100). Defining
 * a field is a layout decision; setting one on a task is ordinary work. An
 * editor who could add options would grow the vocabulary one card at a time,
 * which is exactly what makes free text unusable and what closed options exist
 * to prevent. Nothing here re-checks that in the browser — the database is the
 * check, and a second one here would be a place for the two to disagree.
 *
 * ── Deleting says what it will do ──────────────────────────────────────────
 *
 * task_field_values cascades from both. Deleting a field clears it from every
 * task that held it; deleting an option clears that option from every card
 * carrying it. That is the honest behaviour — values pointing at a label that
 * no longer exists would be worse — and it is why the UI counts the affected
 * cards before asking.
 */

const POSITION_STEP = 1000;

/**
 * Where the next field goes: after the last one there is.
 *
 * Read rather than counted. Counting gives every field created after a
 * deletion the position of one that still exists, and the order on a card then
 * becomes whatever the query happened to return — the sort of thing that looks
 * stable right up until a board has four fields.
 */
async function nextFieldPosition(client: SupabaseClient, projectId: string): Promise<number> {
  const { data, error } = await client
    .from('project_fields')
    .select('position')
    .eq('project_id', projectId)
    .order('position', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    throw new Error(error.message);
  }
  return (data?.position ?? 0) + POSITION_STEP;
}

/** The same, for the options within one field. */
async function nextOptionPosition(client: SupabaseClient, fieldId: string): Promise<number> {
  const { data, error } = await client
    .from('project_field_options')
    .select('position')
    .eq('field_id', fieldId)
    .order('position', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    throw new Error(error.message);
  }
  return (data?.position ?? 0) + POSITION_STEP;
}

export interface NewField {
  projectId: string;
  name: string;
  isMulti: boolean;
  /** Created together: a field with no options is an empty dropdown. */
  options: string[];
}

async function createField(
  client: SupabaseClient,
  actorId: string,
  input: NewField,
): Promise<void> {
  const { data, error } = await client
    .from('project_fields')
    .insert({
      project_id: input.projectId,
      name: input.name.trim(),
      is_multi: input.isMulti,
      // Appended, not inserted: a new field belongs after the ones already
      // there rather than jostling them.
      position: await nextFieldPosition(client, input.projectId),
      created_by: actorId,
    })
    .select('id')
    .single();
  if (error) {
    throw new Error(error.message);
  }

  const options = input.options.map((label) => label.trim()).filter((label) => label !== '');
  if (options.length === 0) {
    return;
  }
  const { error: optionError } = await client.from('project_field_options').insert(
    options.map((label, index) => ({
      field_id: data.id,
      label,
      color_key: DEFAULT_FIELD_COLOR,
      position: (index + 1) * POSITION_STEP,
      created_by: actorId,
    })),
  );
  if (optionError) {
    throw new Error(optionError.message);
  }
}

export function useCreateField(projectId: string) {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const actorId = access.status === 'approved' ? access.profile.id : '';
  return useMutation({
    mutationFn: (input: Omit<NewField, 'projectId'>) =>
      createField(client, actorId, { ...input, projectId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.all });
    },
  });
}

export function useRenameField(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { fieldId: string; name: string }) => {
      const { error } = await client
        .from('project_fields')
        .update({ name: input.name.trim() })
        .eq('id', input.fieldId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.definitions(projectId) });
    },
  });
}

export function useDeleteField(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fieldId: string) => {
      const { error } = await client.from('project_fields').delete().eq('id', fieldId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      // Values cascade, so both queries are stale.
      void queryClient.invalidateQueries({ queryKey: fieldKeys.all });
      void queryClient.invalidateQueries({ queryKey: fieldKeys.values(projectId) });
    },
  });
}

export function useAddOption(projectId: string) {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const actorId = access.status === 'approved' ? access.profile.id : '';
  return useMutation({
    mutationFn: async (input: { fieldId: string; label: string; colorKey?: string }) => {
      const { error } = await client.from('project_field_options').insert({
        field_id: input.fieldId,
        label: input.label.trim(),
        color_key: input.colorKey ?? DEFAULT_FIELD_COLOR,
        position: await nextOptionPosition(client, input.fieldId),
        created_by: actorId,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.definitions(projectId) });
    },
  });
}

/**
 * Recolouring an option.
 *
 * Its own mutation rather than a general option update, because colour is the
 * only thing about an option that is safe to change in place. The LABEL is
 * not: cards carry the option by id, so renaming one silently rewrites what
 * every card holding it appears to say, and the vocabulary a board agreed on
 * changes under it. Colour changes nothing about meaning.
 *
 * Only the DEFINITIONS query is invalidated, not fieldKeys.all. Chip colours
 * are read from the definition; task_field_values holds ids, so nothing about
 * which cards hold what has moved, and refetching the values would be work
 * done to reach the same answer.
 */
export function useSetOptionColor(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { optionId: string; colorKey: string }) => {
      const { error } = await client
        .from('project_field_options')
        .update({ color_key: input.colorKey })
        .eq('id', input.optionId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.definitions(projectId) });
    },
  });
}

export function useDeleteOption(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (optionId: string) => {
      const { error } = await client.from('project_field_options').delete().eq('id', optionId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fieldKeys.all });
      void queryClient.invalidateQueries({ queryKey: fieldKeys.values(projectId) });
    },
  });
}

/**
 * Why a duplicate name is refused before the database refuses it.
 *
 * project_fields has a case-insensitive unique index per project, so this is
 * belt and braces — but the database's error is a constraint name, and a
 * person typing "status" under an existing "Status" deserves a sentence rather
 * than `project_fields_project_name_idx`.
 */
export function nameProblem(name: string, existing: string[]): string | null {
  const clean = name.trim();
  if (clean === '') {
    return 'Give the field a name.';
  }
  if (existing.some((other) => other.toLowerCase() === clean.toLowerCase())) {
    return `This project already has a field called “${clean}”.`;
  }
  return null;
}

/** The same check for an option, within one field. */
export function optionProblem(label: string, existing: string[]): string | null {
  const clean = label.trim();
  if (clean === '') {
    return 'Give the option a name.';
  }
  if (existing.some((other) => other.toLowerCase() === clean.toLowerCase())) {
    return `This field already has “${clean}”.`;
  }
  return null;
}

/**
 * How many CARDS a field and each of its options are actually on.
 *
 * This is what turns "Remove field" from a shrug into a decision. Deleting a
 * field clears it from every task that held it, and the honest way to ask is
 * with the number attached.
 *
 * Distinct tasks, not rows. A multi-select field gives one card several rows —
 * a post tagged Instagram, TikTok and YouTube is three rows in Platform — and
 * counting rows would warn somebody about twelve cards when there are four.
 */
export interface FieldUsage {
  /** field_id → number of cards holding any value for it. */
  byField: Map<string, number>;
  /** option_id → number of cards carrying that option. */
  byOption: Map<string, number>;
}

export function fieldUsage(values: FieldValueRow[]): FieldUsage {
  const fieldTasks = new Map<string, Set<string>>();
  const optionTasks = new Map<string, Set<string>>();

  for (const value of values) {
    const forField = fieldTasks.get(value.field_id) ?? new Set<string>();
    forField.add(value.task_id);
    fieldTasks.set(value.field_id, forField);

    const forOption = optionTasks.get(value.option_id) ?? new Set<string>();
    forOption.add(value.task_id);
    optionTasks.set(value.option_id, forOption);
  }

  const count = (source: Map<string, Set<string>>) =>
    new Map([...source].map(([key, tasks]) => [key, tasks.size]));

  return { byField: count(fieldTasks), byOption: count(optionTasks) };
}

/** "3 cards" / "1 card" / "no cards" — the phrase a delete prompt needs. */
export function cardCountLabel(count: number): string {
  if (count === 0) {
    return 'no cards';
  }
  return count === 1 ? '1 card' : `${String(count)} cards`;
}
